import { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TaskList from '../components/TaskList.tsx';
import BranchSelector from '../components/BranchSelector';
import CreateTaskAttachmentList from '../components/CreateTaskAttachmentList';
import { ImageLightbox, type ImageLightboxItem } from '../components/AttachmentPreview.tsx';
import type { ListTasksResponse, Task } from '../types/task';
import type { Environment, RepoMap } from '../types/environment';
import {
    loadStoredTaskSelection,
    saveStoredTaskSelection,
    loadStoredCreateTaskPrompt,
    saveStoredCreateTaskPrompt,
    clearStoredCreateTaskPrompt,
    clearStoredCreateTaskAttachments,
    saveStoredTaskReasoningLevel,
    loadStoredCreateTaskAttachments,
    saveStoredCreateTaskAttachments,
    type StoredCreateTaskAttachment,
} from '../utils/createTaskStorage';
import { parseErrorResponse, runnerErrorMessage } from '../utils/errors';
import { fetchDefaultBranches } from '../utils/defaultBranches';
import { useFeatures } from '../context/FeatureFlagContext';
import {
    ALL_MODELS,
    LEGACY_MODELS,
    MODEL_ALIASES,
    NEW_AGENT_DEFAULT_MODEL,
    NEW_AGENT_MODELS,
    REASONING_LEVELS,
} from '../components/taskModelOptions';

const MAX_CREATE_TASK_ATTACHMENTS = 25;
const MAX_ACTIVE_CREATE_TASK_ATTACHMENTS = 4;
const CREATE_TASK_ATTACHMENT_SIZE_LIMIT_BYTES = 30 * 1024 * 1024;
const TOO_MANY_ATTACHMENTS_ERROR = 'Too many attachments';
const CREATE_TASK_ATTACHMENT_POLL_INTERVAL_MS = 2000;

const TASK_PAGE_SIZE = 10;

function compareTasksByApiOrder(a: Task, b: Task) {
    const aTime = new Date(a.CreatedAt).getTime();
    const bTime = new Date(b.CreatedAt).getTime();
    const dateDiff = bTime - aTime;
    if (dateDiff !== 0 && !Number.isNaN(dateDiff)) {
        return dateDiff;
    }
    const titleDiff = b.Title.localeCompare(a.Title);
    if (titleDiff !== 0) {
        return titleDiff;
    }
    return b.TaskID.localeCompare(a.TaskID);
}

function mergeTaskListsForRefresh(
    oldList: Task[],
    refreshedList: Task[],
    removedTaskIDs: Set<string>,
    topItem?: Task,
    bottomItem?: Task,
) {
    const shouldSkip = (task: Task) => removedTaskIDs.has(task.TaskID);

    if (!topItem || !bottomItem) {
        const mergedFallback =
            refreshedList.length > 0 ? refreshedList.filter((t) => !shouldSkip(t)) : oldList.filter((t) => !shouldSkip(t));
        return {
            merged: mergedFallback,
            useRefreshedPaging: computeUseRefreshedPaging(mergedFallback, refreshedList, removedTaskIDs, 0),
        };
    }

    const prefix: Task[] = [];
    const middle: Task[] = [];
    const suffix: Task[] = [];

    oldList.forEach((task) => {
        if (shouldSkip(task)) {
            return;
        }
        if (compareTasksByApiOrder(task, topItem) < 0) {
            prefix.push(task);
            return;
        }
        if (compareTasksByApiOrder(task, bottomItem) > 0) {
            suffix.push(task);
            return;
        }
        middle.push(task);
    });

    const mergedMiddle: Task[] = [];
    let oldIdx = 0;
    let newIdx = 0;

    while (oldIdx < middle.length && newIdx < refreshedList.length) {
        const oldTask = middle[oldIdx];
        const newTask = refreshedList[newIdx];
        if (shouldSkip(newTask)) {
            newIdx += 1;
            continue;
        }
        if (oldTask.TaskID === newTask.TaskID) {
            mergedMiddle.push((oldTask.Version ?? 0) >= (newTask.Version ?? 0) ? oldTask : newTask);
            oldIdx += 1;
            newIdx += 1;
            continue;
        }
        const cmp = compareTasksByApiOrder(oldTask, newTask);
        if (cmp < 0) {
            oldIdx += 1;
        } else {
            mergedMiddle.push(newTask);
            newIdx += 1;
        }
    }

    while (newIdx < refreshedList.length) {
        const candidate = refreshedList[newIdx];
        if (!shouldSkip(candidate)) {
            mergedMiddle.push(candidate);
        }
        newIdx += 1;
    }

    const merged = [...prefix, ...mergedMiddle, ...suffix];
    const useRefreshedPaging = computeUseRefreshedPaging(merged, refreshedList, removedTaskIDs, suffix.length);

    return { merged, useRefreshedPaging };
}

function computeUseRefreshedPaging(
    merged: Task[],
    refreshedList: Task[],
    removedTaskIDs: Set<string>,
    suffixLength: number,
) {
    if (suffixLength > 0) {
        return false;
    }
    const filteredRefreshed = refreshedList.filter((t) => !removedTaskIDs.has(t.TaskID));
    if (filteredRefreshed.length === 0) {
        return true;
    }
    const lastRefreshed = filteredRefreshed[filteredRefreshed.length - 1];
    const lastMerged = merged[merged.length - 1];
    return Boolean(lastRefreshed && lastMerged && lastMerged.TaskID === lastRefreshed.TaskID);
}

type UploadTicket = {
    fileID: string;
    uploadURL: string;
};

type FileRecord = {
    FileID: string;
    ContentType?: string | null;
    IsMalicious?: boolean | null;
    MalwareScanCompletedAt?: string | null;
    ModerationScanCompletedAt?: string | null;
    RejectionReason?: string | null;
};

function isAttachmentTerminal(attachment: StoredCreateTaskAttachment): boolean {
    return attachment.status === 'ready'
        || attachment.status === 'error'
        || attachment.status === 'too_large'
        || attachment.status === 'too_many_attachments';
}

function isAttachmentSubmissionBlocking(attachment: StoredCreateTaskAttachment): boolean {
    return attachment.status !== 'ready';
}

function getAttachmentErrorFromFile(file: FileRecord): string | null {
    if (file.IsMalicious === true) {
        return 'File rejected as malicious';
    }
    if (typeof file.RejectionReason === 'string' && file.RejectionReason.length > 0) {
        return file.RejectionReason;
    }
    return null;
}

function isReadyFileRecord(file: FileRecord): boolean {
    return file.IsMalicious === false
        && Boolean(file.MalwareScanCompletedAt)
        && typeof file.ContentType === 'string'
        && file.ContentType.length > 0
        && !file.RejectionReason;
}

export default function CreateTaskPage() {
    const features = useFeatures();
    const visibleModels = features['NewAgent'] ? NEW_AGENT_MODELS : LEGACY_MODELS;
    const defaultModel = features['NewAgent'] ? NEW_AGENT_DEFAULT_MODEL : LEGACY_MODELS[0];
    const [prompt, setPrompt] = useState(() => loadStoredCreateTaskPrompt());
    const [submitting, setSubmitting] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const spinnerShownAt = useRef(0);
    const spinnerTimeout = useRef<number | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [model, setModel] = useState(() => {
        const saved = loadStoredTaskSelection();
        if (saved?.model) {
            const normalizedModel = saved.model === 'Claude 4.6 Opus (1M)' ? 'Claude 4.6 Opus' : saved.model;
            // Check if the saved model is valid
            if (ALL_MODELS.includes(normalizedModel)) {
                return normalizedModel;
            }
            // Check if it's an old model name that needs migration
            const migrated = MODEL_ALIASES[normalizedModel];
            if (migrated && ALL_MODELS.includes(migrated)) {
                return migrated;
            }
        }
        return defaultModel;
    });
    const [reasoningLevel, setReasoningLevel] = useState(() => {
        const saved = loadStoredTaskSelection();
        if (saved?.reasoningLevel && REASONING_LEVELS.some(r => r.value === saved.reasoningLevel)) {
            return saved.reasoningLevel;
        }
        return 'Medium';
    });
    const [attachments, setAttachments] = useState(() => loadStoredCreateTaskAttachments());
    const [lightboxItem, setLightboxItem] = useState<ImageLightboxItem | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const attachmentFilesRef = useRef(new Map<string, File>());
    const attachmentUploadRequestsRef = useRef(new Map<string, XMLHttpRequest>());
    const attachmentPollTimeoutsRef = useRef(new Map<string, number>());
    const attachmentPollingIDsRef = useRef(new Set<string>());
    const attachmentProcessingIDsRef = useRef(new Set<string>());
    const removedAttachmentIDsRef = useRef(new Set<string>());
    const [tasks, setTasks] = useState<Task[]>([]);
    const [nextTaskToken, setNextTaskToken] = useState('');
    const [hasMoreTasks, setHasMoreTasks] = useState(false);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const [envLoading, setEnvLoading] = useState(true);
    const [showEnvSpinner, setShowEnvSpinner] = useState(false);
    const envSpinnerTimeout = useRef<number | null>(null);
    const [envID, setEnvID] = useState('');
    const [branches, setBranches] = useState<Record<string, string>>({});
    const [defaultBranches, setDefaultBranches] = useState<Record<string, string>>({});
    const navigate = useNavigate();
    const location = useLocation();
    const labelStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    } as const;
    const labelTextStyle = {
        fontWeight: 'bold',
        marginBottom: '4px',
    } as const;
    const controlWrapperStyle = {
        display: 'flex',
        alignItems: 'center',
        padding: '6px',
        boxSizing: 'border-box',
        width: '100%',
    } as const;
    const fetchingTasksRef = useRef(false);
    const tasksRef = useRef<Task[]>([]);
    const refreshIntervalRef = useRef<number | null>(null);
    const refreshGenerationRef = useRef(0);
    const refreshingRef = useRef(false);
    const removedTaskIDsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (prompt) {
            saveStoredCreateTaskPrompt(prompt);
        } else {
            clearStoredCreateTaskPrompt();
        }
    }, [prompt]);

    useEffect(() => {
        saveStoredCreateTaskAttachments(attachments);
    }, [attachments]);

    useEffect(() => {
        tasksRef.current = tasks;
    }, [tasks]);

    const clearAttachmentPolling = useCallback((clientID: string) => {
        const timeout = attachmentPollTimeoutsRef.current.get(clientID);
        if (timeout !== undefined) {
            window.clearTimeout(timeout);
            attachmentPollTimeoutsRef.current.delete(clientID);
        }
        attachmentPollingIDsRef.current.delete(clientID);
    }, []);

    const cancelAttachmentUpload = useCallback((clientID: string) => {
        const request = attachmentUploadRequestsRef.current.get(clientID);
        if (request) {
            request.abort();
            attachmentUploadRequestsRef.current.delete(clientID);
        }
    }, []);

    const markAttachmentRemoved = useCallback((clientID: string) => {
        removedAttachmentIDsRef.current.add(clientID);
        attachmentFilesRef.current.delete(clientID);
        attachmentProcessingIDsRef.current.delete(clientID);
        cancelAttachmentUpload(clientID);
        clearAttachmentPolling(clientID);
    }, [cancelAttachmentUpload, clearAttachmentPolling]);

    const isAttachmentRemoved = useCallback((clientID: string) => removedAttachmentIDsRef.current.has(clientID), []);

    const updateAttachment = useCallback((clientID: string, updater: (attachment: StoredCreateTaskAttachment) => StoredCreateTaskAttachment) => {
        let updated = false;
        setAttachments((current) => current.map((attachment) => {
            if (attachment.clientID !== clientID) {
                return attachment;
            }
            updated = true;
            return updater(attachment);
        }));
        return updated;
    }, []);

    const createFileForAttachment = useCallback(async (attachment: StoredCreateTaskAttachment): Promise<UploadTicket> => {
        const fileID = crypto.randomUUID();
        const response = await fetch(`/api/files/${encodeURIComponent(fileID)}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                Name: attachment.name,
                Size: attachment.size,
            }),
        });
        if (!response.ok) {
            const message = await response.text();
            throw new Error(message || 'CreateFile failed');
        }
        const data = await response.json() as { FileID?: string; UploadURL?: string };
        if (typeof data.FileID !== 'string' || typeof data.UploadURL !== 'string') {
            throw new Error('CreateFile returned an invalid response');
        }
        return { fileID: data.FileID, uploadURL: data.UploadURL };
    }, []);

    const uploadAttachmentToS3 = useCallback((clientID: string, file: File, uploadURL: string) => new Promise<void>((resolve, reject) => {
        const request = new XMLHttpRequest();
        attachmentUploadRequestsRef.current.set(clientID, request);
        request.open('PUT', uploadURL, true);
        request.setRequestHeader('If-None-Match', '*');

        request.upload.onprogress = (event) => {
            if (!event.lengthComputable || isAttachmentRemoved(clientID)) {
                return;
            }
            const progress = event.total > 0 ? (event.loaded / event.total) * 100 : 0;
            updateAttachment(clientID, (attachment) => ({ ...attachment, status: 'uploading', progress }));
        };

        request.onload = () => {
            attachmentUploadRequestsRef.current.delete(clientID);
            if (request.status >= 200 && request.status < 300) {
                resolve();
                return;
            }
            reject(new Error(`Upload failed with status ${request.status}`));
        };

        request.onerror = () => {
            attachmentUploadRequestsRef.current.delete(clientID);
            reject(new Error('Upload failed'));
        };

        request.onabort = () => {
            attachmentUploadRequestsRef.current.delete(clientID);
            reject(new DOMException('Upload aborted', 'AbortError'));
        };

        request.send(file);
    }), [isAttachmentRemoved, updateAttachment]);

    const pollAttachmentFile = useCallback(async (clientID: string, fileID: string) => {
        if (attachmentPollingIDsRef.current.has(clientID)) {
            return;
        }

        attachmentPollingIDsRef.current.add(clientID);
        clearAttachmentPolling(clientID);

        if (isAttachmentRemoved(clientID)) {
            attachmentPollingIDsRef.current.delete(clientID);
            return;
        }

        try {
            const response = await fetch(`/api/files/${encodeURIComponent(fileID)}`);
            if (!response.ok) {
                const message = await response.text();
                throw new Error(message || 'GetFile failed');
            }

            const file = await response.json() as FileRecord;
            if (isAttachmentRemoved(clientID)) {
                return;
            }

            const terminalError = getAttachmentErrorFromFile(file);
            if (terminalError) {
                attachmentFilesRef.current.delete(clientID);
                updateAttachment(clientID, (attachment) => ({
                    ...attachment,
                    fileID,
                    status: 'error',
                    progress: 100,
                    error: terminalError,
                }));
                return;
            }

            if (isReadyFileRecord(file)) {
                attachmentFilesRef.current.delete(clientID);
                updateAttachment(clientID, (attachment) => ({
                    ...attachment,
                    fileID,
                    status: 'ready',
                    progress: 100,
                    contentType: file.ContentType ?? undefined,
                    error: undefined,
                }));
                return;
            }

            updateAttachment(clientID, (attachment) => ({
                ...attachment,
                fileID,
                status: 'scanning',
                progress: 100,
            }));

            const timeout = window.setTimeout(() => {
                attachmentPollTimeoutsRef.current.delete(clientID);
                attachmentPollingIDsRef.current.delete(clientID);
                void pollAttachmentFile(clientID, fileID);
            }, CREATE_TASK_ATTACHMENT_POLL_INTERVAL_MS);
            attachmentPollTimeoutsRef.current.set(clientID, timeout);
        } catch (error) {
            if (isAttachmentRemoved(clientID)) {
                return;
            }
            attachmentFilesRef.current.delete(clientID);
            updateAttachment(clientID, (attachment) => ({
                ...attachment,
                fileID,
                status: 'error',
                progress: attachment.progress,
                error: error instanceof Error ? error.message : 'Polling failed',
            }));
        } finally {
            if (!attachmentPollTimeoutsRef.current.has(clientID)) {
                attachmentPollingIDsRef.current.delete(clientID);
            }
        }
    }, [clearAttachmentPolling, isAttachmentRemoved, updateAttachment]);

    const startAttachmentProcessing = useCallback(async (attachment: StoredCreateTaskAttachment) => {
        if (attachmentProcessingIDsRef.current.has(attachment.clientID)) {
            return;
        }

        const file = attachmentFilesRef.current.get(attachment.clientID);
        if (!file || isAttachmentRemoved(attachment.clientID)) {
            return;
        }

        attachmentProcessingIDsRef.current.add(attachment.clientID);

        try {
            const { fileID, uploadURL } = await createFileForAttachment(attachment);
            if (isAttachmentRemoved(attachment.clientID)) {
                return;
            }

            updateAttachment(attachment.clientID, (currentAttachment) => ({
                ...currentAttachment,
                fileID,
                status: 'uploading',
                progress: 0,
                error: undefined,
            }));

            await uploadAttachmentToS3(attachment.clientID, file, uploadURL);
            if (isAttachmentRemoved(attachment.clientID)) {
                return;
            }

            updateAttachment(attachment.clientID, (currentAttachment) => ({
                ...currentAttachment,
                fileID,
                status: 'scanning',
                progress: 100,
            }));
            await pollAttachmentFile(attachment.clientID, fileID);
        } catch (error) {
            if (isAttachmentRemoved(attachment.clientID)) {
                return;
            }
            const message = error instanceof DOMException && error.name === 'AbortError'
                ? null
                : error instanceof Error
                    ? error.message
                    : 'Upload failed';
            if (!message) {
                return;
            }

            attachmentFilesRef.current.delete(attachment.clientID);
            updateAttachment(attachment.clientID, (currentAttachment) => ({
                ...currentAttachment,
                status: 'error',
                error: message,
            }));
        } finally {
            attachmentProcessingIDsRef.current.delete(attachment.clientID);
        }
    }, [createFileForAttachment, isAttachmentRemoved, pollAttachmentFile, updateAttachment, uploadAttachmentToS3]);

    // Handle archiving / unarchiving of a task. Performs optimistic update by
    // removing the task from local state immediately. In case the backend
    // request fails we revert the change and surface an error banner.
    const handleArchiveToggle = useCallback(
        async (task: Task, currentlyArchived: boolean) => {

            // Optimistically remove the task from UI.
            setTasks((prev) => prev.filter((t) => t.TaskID !== task.TaskID));
            removedTaskIDsRef.current.add(task.TaskID);

            // Perform request.
            try {
                let resp: Response;
                if (currentlyArchived) {
                    resp = await fetch(`/api/tasks/${encodeURIComponent(task.TaskID)}/undelete`, {
                        method: 'POST',
                    });
                } else {
                    resp = await fetch(`/api/tasks/${encodeURIComponent(task.TaskID)}`, {
                        method: 'DELETE',
                    });
                }

                if (!resp.ok) {
                    const msg = await resp.text();
                    throw new Error(msg || 'Request failed');
                }
            } catch (err) {
                // Revert optimistic removal.
                setTasks((prev) => {
                    // Reinstate task and sort according to default ordering:
                    // CreatedAt DESC, Title DESC, TaskID DESC.
                    const list = [...prev, task];
                    list.sort(compareTasksByApiOrder);
                    return list;
                });
                setErrors((e) => [...e, `Failed to ${currentlyArchived ? 'unarchive' : 'archive'} task`]);
            }
        },
        [],
    );

    // Whether we are currently viewing archived (deleted) tasks
    const [showArchived, setShowArchived] = useState(false);

    useEffect(() => {
        removedTaskIDsRef.current = new Set();
    }, [showArchived]);

    // Append new tasks to the list. Pagination guarantees uniqueness across pages, so
    // we can safely concatenate without additional de-duplication.
    const appendTasks = useCallback((newTasks: Task[]) => {
        if (newTasks.length === 0) return;
        setTasks((prev) => [...prev, ...newTasks]);
    }, []);

    const fetchTasksPage = useCallback(async (token?: string, includeDeletedParam = false) => {
        const params = new URLSearchParams();
        params.set('maxResults', String(TASK_PAGE_SIZE));
        if (token) {
            params.set('nextToken', token);
        }
        params.set('includeDeleted', includeDeletedParam ? 'true' : 'false');
        const query = params.toString();
        const resp = await fetch(`/api/tasks?${query}`);
        if (!resp.ok) {
            const msg = await resp.text();
            throw new Error(msg || 'ListTasks failed');
        }
        const data: ListTasksResponse = await resp.json();
        const pageTasks = Array.isArray(data.Tasks) ? data.Tasks : [];
        const next = typeof data.NextToken === 'string' ? data.NextToken : '';
        return { tasks: pageTasks, nextToken: next };
    }, []);

    const loadMoreTasks = useCallback(async () => {
        if (fetchingTasksRef.current || !hasMoreTasks || nextTaskToken === '') {
            return;
        }
        fetchingTasksRef.current = true;
        setLoadingTasks(true);
        try {
            const { tasks: pageTasks, nextToken } = await fetchTasksPage(nextTaskToken, showArchived);
            let tasksToAdd = pageTasks;
            if (showArchived) {
                tasksToAdd = pageTasks.filter((t) => t.Deleted);
                // If we encountered any non-deleted tasks, stop pagination.
                if (tasksToAdd.length < pageTasks.length) {
                    setHasMoreTasks(false);
                    setNextTaskToken('');
                } else {
                    setNextTaskToken(nextToken);
                    setHasMoreTasks(nextToken !== '');
                }
            } else {
                setNextTaskToken(nextToken);
                setHasMoreTasks(nextToken !== '');
            }
            appendTasks(tasksToAdd);
        } catch (err) {
            console.error('ListTasks error', err);
        } finally {
            fetchingTasksRef.current = false;
            setLoadingTasks(false);
        }
    }, [appendTasks, fetchTasksPage, hasMoreTasks, nextTaskToken, showArchived]);

    const refreshTasks = useCallback(
        async (generation: number) => {
            if (refreshingRef.current || fetchingTasksRef.current) {
                return;
            }
            const removedForRefresh = new Set<string>();
            removedTaskIDsRef.current = removedForRefresh;
            const snapshot = tasksRef.current;
            if (snapshot.length === 0) {
                return;
            }
            const topItem = snapshot[0];
            const bottomItem = snapshot[snapshot.length - 1];
            refreshingRef.current = true;
            try {
                let token: string | undefined;
                let refreshNextToken = '';
                let refreshHasMore = false;
                let stop = false;
                let refreshedTasks: Task[] = [];

                while (!stop) {
                    const { tasks: pageTasks, nextToken } = await fetchTasksPage(token, showArchived);
                    if (generation !== refreshGenerationRef.current) {
                        refreshingRef.current = false;
                        return;
                    }
                    let tasksToAdd = pageTasks;
                    if (showArchived) {
                        tasksToAdd = pageTasks.filter((t) => t.Deleted);
                        if (tasksToAdd.length < pageTasks.length) {
                            refreshNextToken = '';
                            refreshHasMore = false;
                            stop = true;
                        } else {
                            refreshNextToken = nextToken;
                            refreshHasMore = nextToken !== '';
                        }
                    } else {
                        refreshNextToken = nextToken;
                        refreshHasMore = nextToken !== '';
                    }
                    refreshedTasks = refreshedTasks.concat(tasksToAdd);
                    if (!nextToken) {
                        stop = true;
                    }
                    if (!stop) {
                        const lastItem = pageTasks[pageTasks.length - 1];
                        if (!lastItem) {
                            stop = true;
                        } else if (compareTasksByApiOrder(lastItem, bottomItem) >= 0) {
                            stop = true;
                        } else {
                            token = nextToken;
                        }
                    }
                }

                if (generation !== refreshGenerationRef.current) {
                    refreshingRef.current = false;
                    return;
                }

                const { merged, useRefreshedPaging } = mergeTaskListsForRefresh(
                    tasksRef.current,
                    refreshedTasks,
                    removedTaskIDsRef.current,
                    topItem,
                    bottomItem,
                );
                setTasks(merged);
                if (useRefreshedPaging) {
                    setNextTaskToken(refreshNextToken);
                    setHasMoreTasks(refreshHasMore);
                }
            } catch (err) {
                console.error('Refresh tasks error', err);
            } finally {
                refreshingRef.current = false;
            }
        },
        [fetchTasksPage, showArchived],
    );

    useEffect(() => {
        let cancelled = false;
        const loadInitial = async () => {
            fetchingTasksRef.current = true;
            setLoadingTasks(true);
            setTasks([]);
            setNextTaskToken('');
            setHasMoreTasks(false);
            try {
                let token: string | undefined;
                for (let i = 0; i < 2; i += 1) {
                    const { tasks: pageTasks, nextToken } = await fetchTasksPage(token, showArchived);
                    if (cancelled) {
                        return;
                    }
                    let tasksToAdd = pageTasks;
                    if (showArchived) {
                        tasksToAdd = pageTasks.filter((t) => t.Deleted);
                        if (tasksToAdd.length < pageTasks.length) {
                            // encountered active tasks -> stop after this page
                            setHasMoreTasks(false);
                            setNextTaskToken('');
                        } else {
                            setNextTaskToken(nextToken);
                            setHasMoreTasks(nextToken !== '');
                        }
                    } else {
                        setNextTaskToken(nextToken);
                        setHasMoreTasks(nextToken !== '');
                    }
                    appendTasks(tasksToAdd);
                    if (!nextToken) {
                        break;
                    }
                    token = nextToken;
                }
            } catch (err) {
                console.error('ListTasks error', err);
            } finally {
                fetchingTasksRef.current = false;
                if (!cancelled) {
                    setLoadingTasks(false);
                }
            }
        };
        loadInitial();
        return () => {
            cancelled = true;
        };
    }, [appendTasks, fetchTasksPage, showArchived]);

    useEffect(() => {
        const generation = refreshGenerationRef.current + 1;
        refreshGenerationRef.current = generation;
        if (refreshIntervalRef.current !== null) {
            clearInterval(refreshIntervalRef.current);
        }
        refreshIntervalRef.current = window.setInterval(() => {
            refreshTasks(generation);
        }, 5000);
        return () => {
            if (refreshIntervalRef.current !== null) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [refreshTasks, showArchived]);

    const filterBranchesForEnv = useCallback((env: Environment | undefined, branchMap: Record<string, string>) => {
        if (!env) {
            return {};
        }
        const validKeys = new Set(env.Repos.map((repo) => `${repo.Org}/${repo.Repo}`));
        const filtered: Record<string, string> = {};
        Object.entries(branchMap).forEach(([key, value]) => {
            if (validKeys.has(key) && typeof value === 'string') {
                filtered[key] = value;
            }
        });
        return filtered;
    }, []);

    useEffect(() => {
        let cancelled = false;

        setEnvLoading(true);
        setShowEnvSpinner(false);

        if (envSpinnerTimeout.current !== null) {
            clearTimeout(envSpinnerTimeout.current);
        }
        envSpinnerTimeout.current = window.setTimeout(() => {
            if (!cancelled) {
                setShowEnvSpinner(true);
            }
        }, 200);

        async function loadEnvs() {
            try {
                const resp = await fetch(`/api/environments`);
                if (cancelled) return;
                if (resp.ok) {
                    const data: Environment[] = await resp.json();
                    if (cancelled) return;
                    setEnvironments(data);
                    const savedSelection = loadStoredTaskSelection();
                    const envIDs = new Set(data.map((env) => env.EnvironmentID));
                    const stateEnvID = (location.state as any)?.envID as string | undefined;
                    let initialEnvID = '';
                    if (stateEnvID && envIDs.has(stateEnvID)) {
                        initialEnvID = stateEnvID;
                    } else if (savedSelection?.envID && envIDs.has(savedSelection.envID)) {
                        initialEnvID = savedSelection.envID;
                    } else {
                        initialEnvID = data[0]?.EnvironmentID || '';
                    }
                    setEnvID(initialEnvID);
                    const stateBranches = (location.state as any)?.branches as Record<string, string> | undefined;
                    const envForBranches = data.find((env) => env.EnvironmentID === initialEnvID);
                    const initialBranches = filterBranchesForEnv(
                        envForBranches,
                        stateBranches ?? savedSelection?.branches ?? {},
                    );
                    setBranches(initialBranches);
                } else {
                    const msg = await resp.text();
                    if (cancelled) return;
                    setErrors((errs) => [...errs, `ListEnvironments failed: ${msg}`]);
                    setEnvironments([]);
                    setEnvID('');
                }
            } catch (err) {
                console.error('ListEnvironments error', err);
                if (!cancelled) {
                    setErrors((errs) => [...errs, 'ListEnvironments error']);
                    setEnvironments([]);
                    setEnvID('');
                }
            } finally {
                if (envSpinnerTimeout.current !== null) {
                    clearTimeout(envSpinnerTimeout.current);
                    envSpinnerTimeout.current = null;
                }
                if (!cancelled) {
                    setEnvLoading(false);
                    setShowEnvSpinner(false);
                }
            }
        }
        loadEnvs();

        return () => {
            cancelled = true;
            if (envSpinnerTimeout.current !== null) {
                clearTimeout(envSpinnerTimeout.current);
                envSpinnerTimeout.current = null;
            }
        };
    }, [filterBranchesForEnv, location.state]);

    const selectedEnv = envLoading
        ? undefined
        : environments.find((e) => e.EnvironmentID === envID);

    // Fetch default branches from GitHub when environment changes.
    useEffect(() => {
        // Clear stale defaults immediately so the submit-time fallback
        // merge doesn't use entries from the previous environment.
        setDefaultBranches({});
        if (!selectedEnv) return;
        let cancelled = false;
        fetchDefaultBranches(selectedEnv.EnvironmentID, selectedEnv.Repos).then((map) => {
            if (!cancelled) setDefaultBranches(map);
        });
        return () => { cancelled = true; };
    }, [selectedEnv?.EnvironmentID]);

    const setBranch = (org: string, repo: string, br: string) => {
        const key = `${org}/${repo}`;
        setBranches((prev) => ({ ...prev, [key]: br }));
    };

    useEffect(() => {
        if (envLoading) {
            return;
        }
        if (!envID) {
            return;
        }
        const env = environments.find((e) => e.EnvironmentID === envID);
        const relevantBranches = filterBranchesForEnv(env, branches);
        const selectionModel = ALL_MODELS.includes(model) ? model : undefined;
        const selectionReasoningLevel = REASONING_LEVELS.some(r => r.value === reasoningLevel) ? reasoningLevel : undefined;
        saveStoredTaskSelection({ envID, branches: relevantBranches, model: selectionModel, reasoningLevel: selectionReasoningLevel });
    }, [branches, envID, envLoading, environments, filterBranchesForEnv, model, reasoningLevel]);

    const prependTask = useCallback((task: Task) => {
        if (showArchived) return;
        setTasks((prev) => [task, ...prev]);
    }, [showArchived]);

    const handleRemoveAttachment = useCallback((clientID: string) => {
        markAttachmentRemoved(clientID);
        setAttachments((current) => current.filter((attachment) => attachment.clientID !== clientID));
    }, [markAttachmentRemoved]);

    useEffect(() => {
        if (!features['attachments']) {
            return;
        }

        setAttachments((current): StoredCreateTaskAttachment[] => {
            let activeCount = current.filter(
                (attachment) => attachment.status === 'creating' || attachment.status === 'uploading' || attachment.status === 'scanning',
            ).length;
            let changed = false;

            const nextAttachments: StoredCreateTaskAttachment[] = current.map((attachment) => {
                return attachment;
            });

            for (let index = 0; index < nextAttachments.length; index += 1) {
                const attachment = nextAttachments[index];
                if (attachment.status !== 'pending') {
                    if (attachment.status === 'creating' || attachment.status === 'uploading' || attachment.status === 'scanning') {
                        activeCount += 0;
                    }
                    continue;
                }
                if (activeCount >= MAX_ACTIVE_CREATE_TASK_ATTACHMENTS) {
                    continue;
                }
                nextAttachments[index] = {
                    ...attachment,
                    status: 'creating',
                    progress: 0,
                };
                activeCount += 1;
                changed = true;
            }

            return changed ? nextAttachments : current;
        });
    }, [attachments, features]);

    useEffect(() => {
        if (!features['attachments']) {
            return;
        }

        attachments.forEach((attachment) => {
            if (attachment.status === 'creating' && attachmentFilesRef.current.has(attachment.clientID)) {
                void startAttachmentProcessing(attachment);
            }
            if (attachment.status === 'scanning' && attachment.fileID && !attachmentPollTimeoutsRef.current.has(attachment.clientID)) {
                void pollAttachmentFile(attachment.clientID, attachment.fileID);
            }
            if (isAttachmentTerminal(attachment)) {
                attachmentFilesRef.current.delete(attachment.clientID);
                attachmentPollingIDsRef.current.delete(attachment.clientID);
                attachmentProcessingIDsRef.current.delete(attachment.clientID);
                cancelAttachmentUpload(attachment.clientID);
                clearAttachmentPolling(attachment.clientID);
            }
        });
    }, [attachments, cancelAttachmentUpload, clearAttachmentPolling, features, pollAttachmentFile, startAttachmentProcessing]);

    useEffect(() => () => {
        attachmentUploadRequestsRef.current.forEach((request) => request.abort());
        attachmentUploadRequestsRef.current.clear();
        attachmentPollingIDsRef.current.clear();
        attachmentProcessingIDsRef.current.clear();
        attachmentPollTimeoutsRef.current.forEach((timeout) => window.clearTimeout(timeout));
        attachmentPollTimeoutsRef.current.clear();
    }, []);

    const openFilePicker = useCallback(() => {
        if (!fileInputRef.current) {
            return;
        }

        const input = fileInputRef.current;
        input.value = '';

        if (typeof input.showPicker === 'function') {
            input.showPicker();
            return;
        }

        input.click();
    }, []);

    const handleFileSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        if (files.length === 0) {
            return;
        }

        setAttachments((current): StoredCreateTaskAttachment[] => {
            let activeCount = current.filter(
                (attachment) => attachment.status === 'creating' || attachment.status === 'uploading' || attachment.status === 'scanning',
            ).length;

            const nextAttachments = [...current];
            files.forEach((file, index) => {
                if (file.size > CREATE_TASK_ATTACHMENT_SIZE_LIMIT_BYTES) {
                    nextAttachments.push({
                        clientID: crypto.randomUUID(),
                        name: file.name,
                        size: file.size,
                        status: 'too_large',
                        progress: 0,
                        error: 'File too large',
                    });
                    return;
                }

                const attachmentCount = current.length + index;
                if (attachmentCount >= MAX_CREATE_TASK_ATTACHMENTS) {
                    nextAttachments.push({
                        clientID: crypto.randomUUID(),
                        name: file.name,
                        size: file.size,
                        status: 'too_many_attachments',
                        progress: 0,
                        error: TOO_MANY_ATTACHMENTS_ERROR,
                    });
                    return;
                }

                const status = activeCount < MAX_ACTIVE_CREATE_TASK_ATTACHMENTS ? 'creating' : 'pending';
                if (status === 'creating') {
                    activeCount += 1;
                }
                const clientID = crypto.randomUUID();
                nextAttachments.push({
                    clientID,
                    name: file.name,
                    size: file.size,
                    status,
                    progress: 0,
                });
                attachmentFilesRef.current.set(clientID, file);
            });

            return nextAttachments;
        });

        event.target.value = '';
    }, []);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!prompt.trim()) return;

        const readyFileIDs = features['attachments']
            ? attachments
                .filter((attachment) => attachment.status === 'ready' && typeof attachment.fileID === 'string' && attachment.fileID.length > 0)
                .map((attachment) => attachment.fileID as string)
            : [];

        setSubmitting(true);
        spinnerTimeout.current = window.setTimeout(() => {
            setShowSpinner(true);
            spinnerShownAt.current = Date.now();
        }, 200);
        try {
            const taskID = crypto.randomUUID();
            // Always fetch default branches at submit time to avoid using
            // stale data from a previous environment selection. Merge with
            // cached defaults so a transient API failure doesn't lose them.
            const fresh = selectedEnv
                ? await fetchDefaultBranches(selectedEnv.EnvironmentID, selectedEnv.Repos)
                : {};
            const defaults = { ...defaultBranches, ...fresh };
            const repoInfo: RepoMap = {};
            if (selectedEnv) {
                selectedEnv.Repos.forEach((repo) => {
                    const key = `${repo.Org}/${repo.Repo}`;
                    repoInfo[key] = {
                        TargetBranch: branches[key] || defaults[key] || 'main',
                        FeatureBranch: '',
                    };
                });
            }
            const apiModel = model;
            const apiPrompt = prompt;

            const resp = await fetch(`/api/tasks/${taskID}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    Prompt: apiPrompt,
                    EnvironmentID: envID,
                    RepoInfo: repoInfo,
                    ModelName: apiModel,
                    ReasoningLevel: reasoningLevel || undefined,
                    FileIDs: readyFileIDs,
                }),
            });

            if (resp.ok) {
                const data: Task = await resp.json();
                console.log('Task created:', data);
                setPrompt('');
                setAttachments([]);
                clearStoredCreateTaskPrompt();
                clearStoredCreateTaskAttachments();
                // Save reasoning level to localStorage so it persists when viewing the task
                if (reasoningLevel) {
                    saveStoredTaskReasoningLevel(taskID, reasoningLevel);
                }
                prependTask(data);
            } else {
                const { message, errorType } = await parseErrorResponse(resp);
                console.error('CreateTask failed', resp.status, message, errorType);
                const runnerMessage = runnerErrorMessage(errorType);
                if (runnerMessage) {
                    setErrors((errs) => [...errs, runnerMessage]);
                    return;
                }
                const fallback = message ? `CreateTask failed: ${message}` : 'CreateTask failed.';
                setErrors((errs) => [...errs, fallback]);
            }
        } catch (err) {
            console.error('CreateTask error', err);
            setErrors((errs) => [...errs, 'CreateTask error']);
        } finally {
            const hide = () => {
                if (spinnerTimeout.current !== null) {
                    clearTimeout(spinnerTimeout.current);
                }
                setShowSpinner(false);
                setSubmitting(false);
            };
            if (showSpinner) {
                const elapsed = Date.now() - spinnerShownAt.current;
                const delay = elapsed < 500 ? 500 - elapsed : 0;
                setTimeout(hide, delay);
            } else {
                hide();
            }
        }
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div
                style={{
                    position: 'fixed',
                    top: '60px',
                    left: 0,
                    right: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    zIndex: 1000,
                }}
            >
                {errors.map((err, idx) => (
                    <div
                        key={idx}
                        className="error-banner"
                        style={{
                            background: '#fdd',
                            padding: '8px',
                            marginBottom: '8px',
                            width: '100%',
                            maxWidth: '1024px',
                        }}
                    >
                        {err}
                        <button
                            type="button"
                            onClick={() =>
                                setErrors((e) => e.filter((_, i) => i !== idx))
                            }
                            className="error-banner__close"
                            aria-label="Dismiss error"
                        >
                            &times;
                        </button>
                    </div>
                ))}
            </div>
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingTop: '10vh',
                }}
            >
                <form
                    onSubmit={handleSubmit}
                    style={{
                        width: '100%',
                        maxWidth: '1024px',
                        margin: '0 auto',
                        padding: '20px',
                        position: 'relative',
                    }}
                >
                    <h1 style={{ textAlign: 'center' }}>What do you want to do today?</h1>
                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        disabled={submitting}
                        style={{
                            width: '100%',
                            padding: '8px',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            height: '30vh',
                            minHeight: '120px',
                        }}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px', width: '100%' }}>
                        <label style={labelStyle}>
                            <span style={labelTextStyle}>Environment</span>
                            {envLoading ? (
                                showEnvSpinner ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '6px',
                                            boxSizing: 'border-box',
                                            width: '100%',
                                        }}
                                    >
                                        <div
                                            aria-label="Loading environments"
                                            style={{
                                                border: '4px solid #ccc',
                                                borderTop: '4px solid #333',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                animation: 'spin 1s linear infinite',
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div style={{ height: '32px', width: '100%' }} />
                                )
                            ) : environments.length > 0 ? (
                                <select
                                    value={envID}
                                    onChange={(e) => setEnvID(e.target.value)}
                                    disabled={submitting}
                                    style={{ padding: '6px', boxSizing: 'border-box', width: '100%' }}
                                >
                                    {environments.map((env) => (
                                        <option key={env.EnvironmentID} value={env.EnvironmentID}>
                                            {env.Name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate('/environments');
                                    }}
                                >
                                    Configure Environments
                                </a>
                            )}
                        </label>
                        <label style={labelStyle}>
                            <span style={labelTextStyle}>
                                {selectedEnv?.Repos.length === 1 ? 'Branch' : 'Branches'}
                            </span>
                            {selectedEnv && selectedEnv.Repos.length === 1 ? (
						<BranchSelector
							environmentID={selectedEnv.EnvironmentID}
							org={selectedEnv.Repos[0].Org}
							repo={selectedEnv.Repos[0].Repo}
							value={branches[`${selectedEnv.Repos[0].Org}/${selectedEnv.Repos[0].Repo}`] || defaultBranches[`${selectedEnv.Repos[0].Org}/${selectedEnv.Repos[0].Repo}`] || 'main'}
							onChange={(b) => setBranch(selectedEnv.Repos[0].Org, selectedEnv.Repos[0].Repo, b)}
						/>
                            ) : selectedEnv ? (
                                <div style={controlWrapperStyle}>
                                    <a
                                        className='menu-item-link'
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate('/tasks/new/branches', { state: { env: selectedEnv, branches, envID } });
                                        }}
                                    >
                                        Configure Branches...
                                    </a>
                                </div>
                            ) : (
                                <div style={controlWrapperStyle} />
                            )}
                        </label>
                        <label style={labelStyle}>
                            <span style={labelTextStyle}>Model</span>
                            <select
                                value={model}
                                onChange={(e) => setModel(e.target.value)}
                                disabled={submitting}
                                style={{ padding: '6px', boxSizing: 'border-box', width: '100%' }}
                            >
                                {visibleModels.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {features['NewAgent'] && (
                            <label style={labelStyle}>
                                <span style={labelTextStyle}>Reasoning</span>
                                <select
                                    value={reasoningLevel}
                                    onChange={(e) => setReasoningLevel(e.target.value)}
                                    disabled={submitting}
                                    style={{ padding: '6px', boxSizing: 'border-box', width: '100%' }}
                                >
                                    {REASONING_LEVELS.map((r) => (
                                        <option key={r.value} value={r.value}>
                                            {r.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>
                    {features['attachments'] && (
                        <div style={{ marginTop: '12px' }}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                style={{
                                    position: 'absolute',
                                    width: '1px',
                                    height: '1px',
                                    padding: 0,
                                    margin: '-1px',
                                    overflow: 'hidden',
                                    clip: 'rect(0, 0, 0, 0)',
                                    whiteSpace: 'nowrap',
                                    border: 0,
                                }}
                                onChange={handleFileSelection}
                                disabled={submitting || attachments.length >= MAX_CREATE_TASK_ATTACHMENTS}
                                aria-hidden="true"
                                tabIndex={-1}
                            />
                            <CreateTaskAttachmentList
                                attachments={attachments}
                                onRemoveAttachment={handleRemoveAttachment}
                                onOpenImage={(item) => setLightboxItem(item)}
                            />
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: features['attachments'] ? 'space-between' : 'flex-end',
                            alignItems: 'center',
                            marginTop: '8px',
                            gap: '12px',
                        }}
                    >
                        {features['attachments'] && (
                            <button
                                type="button"
                                className="create-task-upload-button"
                                disabled={submitting || attachments.length >= MAX_CREATE_TASK_ATTACHMENTS}
                                onClick={() => {
                                    openFilePicker();
                                }}
                            >
                                <span className="create-task-upload-button__icon" aria-hidden="true">+</span>
                                Upload File
                            </button>
                        )}
                        <button
                            type='submit'
                            disabled={submitting || envLoading || environments.length === 0 || (features['attachments'] && attachments.some(isAttachmentSubmissionBlocking))}
                            style={{ padding: '6px 12px' }}
                        >
                            Submit
                        </button>
                    </div>
                    {showSpinner && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(255,255,255,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <div
                                style={{
                                    border: '4px solid #ccc',
                                    borderTop: '4px solid #333',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    animation: 'spin 1s linear infinite',
                                }}
                            />
                        </div>
                    )}
                    <style>
                        {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
                    </style>
                    {features['attachments'] && lightboxItem && (
                        <ImageLightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />
                    )}
                </form>
                    <div style={{ width: '100%' }}>
                        <div
                            style={{
                                width: '100%',
                                maxWidth: '1024px',
                                margin: '20px auto',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '16px',
                                fontSize: '20px',
                            }}
                        >
                            {showArchived ? (
                                <>
                                    <a
                                        className="menu-item-link"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setShowArchived(false);
                                        }}
                                    >
                                        Tasks
                                    </a>
                                    <span>|</span>
                                    <strong>Archived</strong>
                                </>
                            ) : (
                                <>
                                    <strong>Tasks</strong>
                                    <span>|</span>
                                    <a
                                        className="menu-item-link"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setShowArchived(true);
                                        }}
                                    >
                                        Archived
                                    </a>
                                </>
                            )}
                        </div>
                        <TaskList
                            tasks={tasks}
                            onLoadMore={loadMoreTasks}
                            hasMore={hasMoreTasks}
                            isLoading={loadingTasks}
                            hideHeader
                            emptyMessage={showArchived ? 'No archived tasks' : 'No tasks yet'}
                            viewingArchived={showArchived}
                            onArchiveToggle={handleArchiveToggle}
                        />
                    </div>
            </div>
        </div>
    );
}
